import { useState, useRef, useEffect } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { Button } from '~/design-system/elements/Button/Button';
import styles from './Question.module.css';

export type QuestionId = string;
export type OptionId = string;
export type QuestionType = 'single' | 'multiple' | 'open';

export interface QuestionOption {
  id: string;
  label: string;
}

export interface QuestionData {
  id: string;
  question: string;
  type: QuestionType;
  options?: QuestionOption[];
}

export interface QuestionAnswer {
  questionId: QuestionId;
  question: string;
  value: string[] | null;
  skipped: boolean;
}

export interface QuestionProps {
  questions: QuestionData[];
  onClose: () => void;
  onSkip: () => void;
  onSubmit: (answers: QuestionAnswer[]) => void;
  isSubmitting?: boolean;
  initialShowSummary?: boolean;
  initialSelectedOptions?: Map<QuestionId, OptionId[]>;
  initialTextInputs?: Map<QuestionId, string>;
  initialCurrentIndex?: number;
}

export const Question = ({
  questions,
  onClose,
  onSkip,
  onSubmit,
  isSubmitting = false,
  initialShowSummary = false,
  initialSelectedOptions = new Map(),
  initialTextInputs = new Map(),
  initialCurrentIndex = 0,
}: QuestionProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialCurrentIndex);
  const [selectedOptions, setSelectedOptions] = useState<Map<QuestionId, OptionId[]>>(() =>
    initialSelectedOptions ? new Map(initialSelectedOptions) : new Map()
  );
  const [textInputs, setTextInputs] = useState<Map<QuestionId, string>>(() =>
    initialTextInputs ? new Map(initialTextInputs) : new Map()
  );
  const [activeCustomInput, setActiveCustomInput] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(initialShowSummary);
  const customInputRef = useRef<HTMLTextAreaElement>(null);

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const totalQuestions = questions.length;
  const hasMultipleQuestions = questions.length > 1;

  const currentOptionIds = selectedOptions.get(currentQuestion.id) || [];
  const currentTextInput = textInputs.get(currentQuestion.id) || '';
  const hasAnswer = currentOptionIds.length > 0 || currentTextInput.trim().length > 0;
  const isCustomInputSelected = currentQuestion.type === 'multiple' && currentTextInput.trim().length > 0;

  const handleOptionSelect = (optionId: string) => {
    const questionId = currentQuestion.id;

    if (currentQuestion.type === 'single') {
      setSelectedOptions((prev) => {
        const n = new Map(prev);
        n.set(questionId, [optionId]);
        return n;
      });
      setTextInputs((prev) => {
        const n = new Map(prev);
        n.set(questionId, '');
        return n;
      });
      setActiveCustomInput(null);
    } else {
      setSelectedOptions((prev) => {
        const n = new Map(prev);
        const current = n.get(questionId) || [];
        if (current.includes(optionId)) {
          n.set(
            questionId,
            current.filter((id) => id !== optionId),
          );
        } else {
          n.set(questionId, [...current, optionId]);
        }
        return n;
      });
      setActiveCustomInput(null);
    }
  };

  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const handleCustomInputChange = (value: string) => {
    const qid = currentQuestion.id;
    setTextInputs((prev) => {
      const n = new Map(prev);
      n.set(qid, value);
      return n;
    });
  };

  useEffect(() => {
    autoResizeTextarea(customInputRef.current);
  }, [currentTextInput]);

  const handleCustomInputFocus = () => {
    setActiveCustomInput(currentQuestion.id);
    if (currentQuestion.type === 'single') {
      const qid = currentQuestion.id;
      setSelectedOptions((prev) => {
        const n = new Map(prev);
        n.set(qid, []);
        return n;
      });
    }
  };

  const handleCustomInputBlur = () => {
    if (!currentTextInput.trim()) {
      setActiveCustomInput(null);
    }
  };

  const handleCustomInputSubmit = () => {
    if (currentTextInput) {
      setActiveCustomInput(null);
    }
  };

  const handleNext = () => {
    if (isLastQuestion) {
      if (hasMultipleQuestions) {
        setShowSummary(true);
      } else {
        handleFinalSubmit();
      }
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleFinalSubmit = () => {
    const questionAnswers: QuestionAnswer[] = questions.map(q => {
      const optionIds = selectedOptions.get(q.id) || [];
      const textInput = textInputs.get(q.id)?.trim() || '';
      const values: string[] = [];

      if (q.type === 'single') {
        if (optionIds.length > 0) {
          const option = q.options?.find(opt => opt.id === optionIds[0]);
          if (option) values.push(option.label);
        } else if (textInput) {
          values.push(textInput);
        }
      } else if (q.type === 'multiple') {
        optionIds.forEach(id => {
          const option = q.options?.find(opt => opt.id === id);
          if (option) values.push(option.label);
        });
        if (textInput) values.push(textInput);
      } else {
        if (textInput) values.push(textInput);
      }

      return {
        questionId: q.id,
        question: q.question,
        value: values.length > 0 ? values : null,
        skipped: values.length === 0,
      };
    });
    onSubmit(questionAnswers);
  };

  const handleEditQuestion = (questionIndex: number) => {
    setShowSummary(false);
    setCurrentIndex(questionIndex);
  };

  const getAnswerText = (questionId: string): string => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return '';

    const optionIds = selectedOptions.get(questionId) || [];
    const textInput = textInputs.get(questionId)?.trim() || '';
    const texts: string[] = [];

    optionIds.forEach(id => {
      const option = question.options?.find(opt => opt.id === id);
      if (option) texts.push(option.label);
    });

    if (textInput) texts.push(textInput);

    return texts.join(' and ');
  };

  const getTotalSelectedCount = (): number => {
    let count = 0;
    questions.forEach(q => {
      const optionIds = selectedOptions.get(q.id) || [];
      const textInput = textInputs.get(q.id)?.trim() || '';
      count += optionIds.length;
      if (textInput) count += 1;
    });
    return count;
  };

  const hasAnyAnswer = (): boolean => {
    return questions.some(q => {
      const optionIds = selectedOptions.get(q.id) || [];
      const textInput = textInputs.get(q.id)?.trim() || '';
      return optionIds.length > 0 || textInput.length > 0;
    });
  };

  const handleSkip = () => {
    if (isLastQuestion) {
      if (hasMultipleQuestions && hasAnyAnswer()) {
        setShowSummary(true);
      } else {
        handleFinalSubmit();
      }
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const isOptionSelected = (optionId: string) => currentOptionIds.includes(optionId);

  if (showSummary) {
    return (
      <div className={styles.question}>
        <div className={styles.header}>
          <p className={styles.questionText}>Summary</p>
        <div className={styles.headerActions}>
          <Button variant="ghostMuted" size="icon-2xs" onClick={onClose} leadIcon="close" disabled={isSubmitting} />
        </div>
        </div>

        <div className={styles.content}>
          {questions.map((question, index) => {
            const answerText = getAnswerText(question.id);
            if (!answerText) return null;
            
            return (
              <button
                key={question.id}
                type="button"
                className={styles.summaryItem}
                onClick={() => handleEditQuestion(index)}
                disabled={isSubmitting}
              >
                <div className={styles.summaryItemContent}>
                  <div className={styles.summaryItemHeader}>
                    <p className={styles.summaryQuestion}>{question.question}</p>
                    <div className={styles.editIcon}>
                      <DeckIcon name="edit" size={16} />
                    </div>
                  </div>
                  <p className={styles.summaryAnswer}>{answerText}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className={styles.actions}>
          <div className={styles.actionsLeft}>
            <span className={styles.selectedCount}>
              {getTotalSelectedCount()} selected
            </span>
          </div>
          <div className={styles.actionsRight}>
            <Button variant="ghostMuted" size="xs" onClick={onSkip} disabled={isSubmitting}>
              Skip
            </Button>
            <Button
              variant="default"
              size="xs"
              onClick={handleFinalSubmit}
              tailIcon="arrow-up"
              disabled={isSubmitting}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.question}>
      <div className={styles.header}>
        <p className={styles.questionText}>{currentQuestion.question}</p>
        <div className={styles.headerActions}>
          {totalQuestions > 1 && (
            <span className={styles.pagination}>{currentIndex + 1} of {totalQuestions}</span>
          )}
          <Button variant="ghostMuted" size="icon-2xs" onClick={onClose} leadIcon="close" disabled={isSubmitting} />
        </div>
      </div>

      <div className={styles.content}>
          {currentQuestion.type === 'open' ? (
            <div className={styles.openInputContainer}>
              <div className={`${styles.optionIcon} ${styles.optionIconBordered}`}>
                <DeckIcon name="edit" size={16} />
              </div>
              <textarea
                className={styles.openInput}
                placeholder="Type your answer..."
                value={currentTextInput}
                onChange={(e) => {
                  handleCustomInputChange(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                rows={1}
                disabled={isSubmitting}
              />
            </div>
          ) : (
            <>
              {currentQuestion.options?.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  className={`${styles.option} ${isOptionSelected(option.id) ? styles.optionSelected : ''}`}
                  onClick={() => handleOptionSelect(option.id)}
                  disabled={isSubmitting}
                >
                  <div className={`${styles.optionIcon} ${currentQuestion.type === 'single' ? styles.optionIconBordered : ''}`}>
                    {currentQuestion.type === 'single' ? (
                      <span className={styles.optionNumber}>{index + 1}</span>
                    ) : (
                      <div className={`${styles.checkbox} ${isOptionSelected(option.id) ? styles.checkboxChecked : ''}`}>
                        {isOptionSelected(option.id) && <DeckIcon name="check" size={14} />}
                      </div>
                    )}
                  </div>
                  <span className={`${styles.optionLabel} ${isOptionSelected(option.id) ? styles.optionLabelSelected : ''}`}>
                    {option.label}
                  </span>
                </button>
              ))}

              <div className={`${styles.customOption} ${(activeCustomInput === currentQuestion.id || currentTextInput.trim().length > 0) ? styles.customOptionActive : ''}`}>
                <div className={`${styles.optionIcon} ${currentQuestion.type === 'single' ? styles.optionIconBordered : ''}`}>
                  {currentQuestion.type === 'single' ? (
                    <DeckIcon name="edit" size={16} />
                  ) : (
                    <div className={`${styles.checkbox} ${isCustomInputSelected ? styles.checkboxChecked : ''}`}>
                      {isCustomInputSelected && <DeckIcon name="check" size={14} />}
                    </div>
                  )}
                </div>
                <textarea
                  ref={customInputRef}
                  className={styles.customInput}
                  placeholder="Something else"
                  value={currentTextInput}
                  onChange={(e) => {
                    handleCustomInputChange(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  onFocus={handleCustomInputFocus}
                  onBlur={handleCustomInputBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleCustomInputSubmit();
                    }
                  }}
                  rows={1}
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
      </div>

      <div className={styles.actions}>
        <div className={styles.actionsLeft}>
          {totalQuestions > 1 && (
            <span className={styles.selectedCount}>
              {currentOptionIds.length > 0 && `${currentOptionIds.length} selected`}
            </span>
          )}
        </div>
        <div className={styles.actionsRight}>
          <Button variant="ghostMuted" size="xs" onClick={handleSkip} disabled={isSubmitting}>
            Skip
          </Button>
          <Button
            variant="default"
            size="xs"
            onClick={handleNext}
            disabled={!hasAnswer || isSubmitting}
            tailIcon={totalQuestions === 1 ? 'arrow-up' : 'arrow-right'}
          >
            {totalQuestions === 1 ? 'Send' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};
